import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { isNil } from 'lodash-es';
import { OrganizationRole } from '../services/application.do';
import { MsalService } from '@azure/msal-angular';
import { ApplicationContextService } from '../services/application-context.service';
import { OrganizationRoleService } from '../services/application.service';
import { Subject, takeUntil } from 'rxjs';

export interface IRoleCopySource {
  roleId: number;
  name: string;
  operationCount: number;
}

export interface IRoleSelectFormParams {
  /** OrganizationRole referenceNames to filter out of the picker (typically the ones already bound by the caller). */
  excludedRoleNames: string[];
  /** Optional: already-assigned roles offered as "copy operations from" sources. */
  copySources?: IRoleCopySource[];
}

/** The dialog always closes with this shape (or undefined on cancel). */
export interface IRoleSelectResult {
  roles: OrganizationRole[];
  copyFromRoleIds: number[];
}

@Component({
  selector: 'app-role-select-form',
  templateUrl: './role-select-form.component.html',
  standalone: false
})
export class RoleSelectFormComponent implements OnInit, OnDestroy {

  private readonly _destroying$ = new Subject<void>();

  selected: OrganizationRole[] = [];
  copyFromRoleIds = new Set<number>();
  canManage = false;

  list: OrganizationRole[];
  private allRoles: OrganizationRole[] = [];

  constructor(
    private dialogRef: MatDialogRef<RoleSelectFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IRoleSelectFormParams,
    private authService: MsalService,
    private organizationService: OrganizationRoleService,
    private applicationContextService: ApplicationContextService
  ) { }

  ngOnInit(): void {
    this.applicationContextService.currentOrganization$
      .pipe(takeUntil(this._destroying$))
      .subscribe(org => {
        if (org) {
          this.loadOrgRoles();
        }
      });
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  onSaveClicked() {
    const result: IRoleSelectResult = { roles: this.selected, copyFromRoleIds: [...this.copyFromRoleIds] };
    this.dialogRef.close(result);
  }

  onCopySourceToggled(source: IRoleCopySource) {
    if (this.copyFromRoleIds.has(source.roleId)) {
      this.copyFromRoleIds.delete(source.roleId);
    } else {
      this.copyFromRoleIds.add(source.roleId);
    }
  }

  isCopySourceSelected(source: IRoleCopySource): boolean {
    return this.copyFromRoleIds.has(source.roleId);
  }

  onCancelClicked() {
    this.dialogRef.close();
  }

  get canAdd() {
    return this.authService.instance.getActiveAccount().tenantId === this.applicationContextService.getCurrentOrganization()?.tenantId;
  }

  loadOrgRoles() {
    this.organizationService.getAll().subscribe(result => {
      this.allRoles = result;
      this.filter();
    });
  }

  filter(fullTextSearch?: string) {
    const excluded = new Set(this.data.excludedRoleNames);
    let filtered = this.allRoles.filter(r => !excluded.has(r.referenceName));
    if (!isNil(fullTextSearch) && fullTextSearch.length > 0) {
      const text = fullTextSearch.toLocaleLowerCase();
      filtered = filtered.filter(r => r.referenceName.toLocaleLowerCase().includes(text));
    }
    this.list = filtered;
  }

  get selectedCount(): number {
    return this.selected.length;
  }

  isSelected(role: OrganizationRole): boolean {
    return this.selected.some(r => r.id === role.id);
  }

  onRoleToggled(role: OrganizationRole) {
    if (this.isSelected(role)) {
      this.selected = this.selected.filter(r => r.id !== role.id);
    } else {
      this.selected = [...this.selected, role];
    }
  }
}
